/**
 * buildCapturePayload (Inventory Screens Step 4.3a) — PURE diff-builder.
 *
 * Turns one field-DB record row (baseline / working / dirty + child arrays)
 * into the per-record sync_field_capture request object:
 *
 *   { tool_id, etag, fields: { <key>: {from, to} },
 *     part_numbers?, removed_part_number_ids?, asset_tags?, removed_asset_tag_ids? }
 *
 * or null when there is nothing to send (empty diff — must never be queued).
 *
 * Locked contracts (4.3a):
 *  - Scalars: every dirty key (minus the child sentinels and the server-owned
 *    exclusion set) emits fields[key] = { from: baseline[key], to: working[key] }.
 *  - SERVER_OWNED fields are NEVER emitted even if dirty — they are computed
 *    by _apply_capture_formulas server-side. `material` is NOT excluded: the
 *    material_source→MANUAL latch is server logic; the client sends it plainly.
 *  - Child sentinel CONTRACT (Step 5's edit path must honor this):
 *      dirty.part_numbers === true → emit part_numbers as the FULL merged set
 *      dirty.asset_tags   === true → emit asset_tags as the FULL merged set
 *    Sentinel absent → key OMITTED (server: absent = children untouched).
 *    The merged set = primary + extra_* overflow — never the visible-10 alone.
 *    The array deletes nothing by omission: since PN-STALE B1 (2026-10-16)
 *    the server deletes ONLY the ids named in removed_*_ids, so an EMPTY array
 *    with the sentinel set sends no children and deletes nothing.
 *  - Removal lists (PN-STALE B2, spec v1.2 §6.2): removed_part_number_ids /
 *    removed_asset_tag_ids carry the row's REMOVAL_LIST_KEYS lists, written by
 *    fieldStore.updateChildSet. Emitted ONLY beside their group's array — the
 *    server reads a list only when that array is present — and omitted when
 *    empty (the server treats absent and empty alike). Sorted, deduplicated.
 *  - Output is value/id-keyed and sorted, never slot-indexed — identical
 *    child sets in different input orders produce identical payloads.
 *  - etag rides from the record's hoisted Load etag (body field, not a header).
 */

// Server-computed fields — belt-and-suspenders: the Step-5 edit path should
// never mark these dirty, but a future bug must not let the client write them.
export const SERVER_OWNED = new Set(['material_source', 'estimated_tool_size', 'calculated_weight']);

// Keys on `dirty` that are child-array sentinels, not scalar field names.
export const CHILD_SENTINELS = new Set(['part_numbers', 'asset_tags']);

// PN-STALE B2: the row-level removal list for each child group — server ids the
// field × removed, absent until the first removal (readers treat absent as
// empty). Written by fieldStore.updateChildSet, carried by mergeRecord, sent
// here. One map, so the three can never disagree on a key name.
export const REMOVAL_LIST_KEYS = {
  part_numbers: 'removed_part_number_ids',
  asset_tags: 'removed_asset_tag_ids',
};

/** Deterministic child ordering: by value, then id — input-order-independent. */
function childSort(a, b) {
  const av = a.value ?? '';
  const bv = b.value ?? '';
  if (av !== bv) return av < bv ? -1 : 1;
  const ai = a.id ?? '';
  const bi = b.id ?? '';
  return ai < bi ? -1 : ai > bi ? 1 : 0;
}

// Exported for reconcileConfirmations (spec v1.3 §2.4): the child-dirty check
// compares the row against the payload through these same two builders, so
// the comparison cannot drift from what was sent.
export function mergedPartNumbers(record) {
  return [...(record.part_numbers || []), ...(record.extra_part_numbers || [])]
    .map((e) => {
      const out = { value: e.value };
      if (e.id) out.id = e.id; // no id = net-new slot → server mints
      if (e.type !== undefined && e.type !== null) out.type = e.type;
      return out;
    })
    .sort(childSort);
}

export function mergedAssetTags(record) {
  return [...(record.asset_tags || []), ...(record.extra_asset_tags || [])]
    .map((e) => {
      const out = { value: e.value };
      if (e.id) out.id = e.id;
      return out;
    })
    .sort(childSort);
}

/** The group's removal list for the wire: deduplicated, sorted, [] when absent. */
function removalList(record, group) {
  return [...new Set(record[REMOVAL_LIST_KEYS[group]] || [])].sort();
}

/**
 * @param {Object} record — a fieldDb records row
 * @returns {Object|null} sync_field_capture per-record request object, or
 *   null when the record carries no scalar diff and no child sentinel.
 */
export function buildCapturePayload(record) {
  const dirty = record.dirty || {};
  const baseline = record.baseline || {};
  const working = record.working || {};

  const fields = {};
  for (const key of Object.keys(dirty)) {
    if (CHILD_SENTINELS.has(key)) continue;
    if (SERVER_OWNED.has(key)) continue;
    fields[key] = { from: baseline[key], to: working[key] };
  }

  const sendPns = dirty.part_numbers === true;
  const sendAts = dirty.asset_tags === true;

  if (Object.keys(fields).length === 0 && !sendPns && !sendAts) {
    return null; // empty diff — mirrors the server's no-op guard
  }

  const payload = {
    tool_id: record.tool_id,
    etag: record.etag,
    fields,
  };
  if (sendPns) {
    payload.part_numbers = mergedPartNumbers(record);
    const removed = removalList(record, 'part_numbers');
    if (removed.length > 0) payload.removed_part_number_ids = removed;
  }
  if (sendAts) {
    payload.asset_tags = mergedAssetTags(record);
    const removed = removalList(record, 'asset_tags');
    if (removed.length > 0) payload.removed_asset_tag_ids = removed;
  }

  return payload;
}

export default buildCapturePayload;
